import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import type { ItemDaBaseEntrada } from "@bookdental/schemas";
import { Card, CardContent } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ErroConsulta } from "@/components/ErroConsulta";
import { EditorDaBase } from "@/components/base/EditorDaBase";

const TIPOS = [
  { tipo: "servicos", rotulo: "Serviços" },
  { tipo: "materiais", rotulo: "Materiais" },
  { tipo: "insumos", rotulo: "Insumos" },
  { tipo: "equipamentos", rotulo: "Equipamentos" },
] as const;
type Tipo = (typeof TIPOS)[number]["tipo"];

/**
 * Candidatos à base: itens que os laboratórios criaram por conta própria, agrupados pelo nome.
 * O que se repete em vários laboratórios é sinal de que falta na base — promover cria o item (em
 * rascunho) e liga as cópias dos laboratórios a ele.
 */
export function AdminBaseCandidatos() {
  const [tipo, setTipo] = useState<Tipo>("servicos");
  const [minimo, setMinimo] = useState(2);
  const [promovendo, setPromovendo] = useState<{ sugestao: ItemDaBaseEntrada; itemIds: string[] } | null>(null);
  const lista = trpc.admin.base.candidatos.useQuery({ tipo, minimo });

  return (
    <div className="mx-auto max-w-[1600px] space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Candidatos à base</h1>
        <p className="mt-1 text-sm text-admin-700">
          O que os laboratórios criaram e ainda não existe na base. Quanto mais laboratórios, mais forte o sinal.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {TIPOS.map((t) => (
            <button
              key={t.tipo}
              type="button"
              onClick={() => setTipo(t.tipo)}
              aria-pressed={tipo === t.tipo}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium",
                tipo === t.tipo ? "border-admin-900 bg-admin-900 text-white" : "border-admin-200 bg-white text-admin-700 hover:bg-admin-50",
              )}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-admin-700">
          <input type="checkbox" checked={minimo === 1} onChange={(e) => setMinimo(e.target.checked ? 1 : 2)} className="accent-admin-900" />
          Mostrar também os de um laboratório só
        </label>
      </div>

      <Card className="border-admin-200">
        <CardContent className="p-0">
          {lista.error ? (
            <div className="p-5">
              <ErroConsulta error={lista.error} />
            </div>
          ) : lista.isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
            </div>
          ) : !lista.data?.length ? (
            <div className="py-16 text-center text-sm text-admin-700">
              {minimo > 1 ? "Nenhum item repetido entre laboratórios." : "Nenhum item criado pelos laboratórios."}
            </div>
          ) : (
            <ul className="divide-y divide-admin-200">
              {lista.data.map((g) => (
                <li key={g.chave} className="flex flex-wrap items-center gap-4 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-admin-900">{g.nome}</p>
                    <p className="truncate text-xs text-admin-700/80">
                      {g.nomesDosLabs.join(", ")}
                      {g.laboratorios > g.nomesDosLabs.length ? ` e mais ${g.laboratorios - g.nomesDosLabs.length}` : ""}
                    </p>
                  </div>
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                    {g.laboratorios} {g.laboratorios === 1 ? "laboratório" : "laboratórios"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPromovendo({ sugestao: g.sugestao, itemIds: g.itemIds })}
                    className="inline-flex h-8 items-center gap-1 rounded-md bg-admin-900 px-3 text-sm font-semibold text-white hover:bg-admin-700"
                  >
                    Promover
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <EditorDaBase
        tipo={tipo}
        id={null}
        aberto={!!promovendo}
        inicial={promovendo?.sugestao}
        ligarItens={promovendo?.itemIds}
        onFechar={() => setPromovendo(null)}
      />
    </div>
  );
}
